import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { SignalementSubject } from "../../lib/signalements";

/**
 * What the "signaler une erreur" flag should report about right now.
 *
 * The flag lives in the app header, the screens live in the outlet below it:
 * a screen declares its subject here, and the header picks it up when the
 * dialog opens. A screen that declares nothing still works — the report then
 * carries the page the user was on, which is what the operator needs to
 * reproduce.
 */
interface SignalementSubjectContextValue {
  subject: SignalementSubject | null;
  setSubject: (subject: SignalementSubject | null) => void;
}

const SignalementSubjectContext = createContext<SignalementSubjectContextValue | null>(null);

export function SignalementSubjectProvider({ children }: { children: React.ReactNode }) {
  const [subject, setSubjectState] = useState<SignalementSubject | null>(null);
  const setSubject = useCallback((next: SignalementSubject | null) => setSubjectState(next), []);
  const value = useMemo(() => ({ subject, setSubject }), [subject, setSubject]);

  return <SignalementSubjectContext.Provider value={value}>{children}</SignalementSubjectContext.Provider>;
}

/** The current subject, or null when the screen did not declare one. */
export function useSignalementSubject(): SignalementSubject | null {
  return useContext(SignalementSubjectContext)?.subject ?? null;
}

/**
 * Declares the thing the screen is about while it is on screen (the flashcard
 * being studied, the curriculum item being edited). Cleared on unmount so the
 * next screen never inherits it.
 *
 * One screen declares at a time: the last declaration wins.
 */
export function useSignalementSubjectRegistration(subject: SignalementSubject | null): void {
  const setSubject = useContext(SignalementSubjectContext)?.setSubject;
  // The caller rebuilds its subject on every render; only its identity matters.
  const key = subject ? `${subject.type}|${subject.id ?? ""}|${subject.label ?? ""}` : "";

  useEffect(() => {
    if (!setSubject) return;
    setSubject(subject);
    return () => setSubject(null);
    // `key` captures everything that identifies the subject.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSubject, key]);
}
